import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TRADES, COVERAGE_STATES, COVERAGE_CITIES, AVAILABILITY_STATUSES, US_STATES } from "@/lib/constants";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  first_name: "", last_name: "", company_name: "", trade: "", trades: [], secondary_trades: "",
  phone: "", email: "", street_address: "", city: "", state: "FL", zip_code: "",
  coverage_states: [], coverage_cities: [], service_radius: "", availability_status: "Available",
  years_experience: "", emergency_availability: false, notes: "", create_login: false, password: "",
};

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
      {children}
    </button>
  );
}

export function AddContractorDialog({ open, onOpenChange, onSaved, existing }) {
  const editing = !!existing;
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target?.value ?? e }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  useEffect(() => {
    if (open) setF(editing ? { ...BLANK, ...existing, trades: existing.trades || [], coverage_states: existing.coverage_states || [], coverage_cities: existing.coverage_cities || [] } : BLANK);
  }, [open, editing, existing]);

  const submit = async () => {
    if (!f.first_name.trim() || !f.last_name.trim()) return toast.error("First and last name are required");
    if (!f.trade) return toast.error("Select a primary trade");
    if (!f.phone.trim()) return toast.error("Phone is required");
    if (!f.email.trim()) return toast.error("Email is required");
    if (!f.city.trim() || !f.state) return toast.error("City and state are required");
    if (!editing && f.create_login && !f.password) return toast.error("Enter a password for the login account");
    setBusy(true);
    try {
      const payload = { ...f, trades: f.trades.length ? f.trades : [f.trade] };
      if (editing) {
        const { first_name, last_name, company_name, trade, trades, secondary_trades, phone, street_address, city, state, zip_code, coverage_states, coverage_cities, service_radius, years_experience, emergency_availability, notes } = payload;
        await api.put(`/admin/contractors/${existing.id}`, { first_name, last_name, company_name, trade, trades, secondary_trades, phone, street_address, city, state, zip_code, coverage_states, coverage_cities, service_radius, years_experience, emergency_availability, notes });
        toast.success("Contractor updated");
      } else {
        await api.post("/admin/contractors", payload);
        toast.success("Contractor added");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const cities = [...(COVERAGE_CITIES[f.state] || []), ...f.coverage_states.flatMap((s) => COVERAGE_CITIES[s] || [])];
  const uniqCities = [...new Set(cities)];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-contractor-modal">
        <DialogHeader>
          <DialogTitle className="font-head">{editing ? "Edit Contractor" : "Add Contractor"}</DialogTitle>
          <DialogDescription>{editing ? "Update contractor details." : "Add a contractor to your field team. They will enter as an Applicant."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <Section title="Basic Information">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="First Name"><Input data-testid="ac-first-name" value={f.first_name} onChange={set("first_name")} /></Field>
              <Field label="Last Name"><Input data-testid="ac-last-name" value={f.last_name} onChange={set("last_name")} /></Field>
              <Field label="Company Name"><Input data-testid="ac-company" value={f.company_name} onChange={set("company_name")} /></Field>
              <Field label="Primary Trade">
                <Select value={f.trade} onValueChange={set("trade")}>
                  <SelectTrigger data-testid="ac-trade"><SelectValue placeholder="Select trade" /></SelectTrigger>
                  <SelectContent className="max-h-64">{TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Phone"><Input data-testid="ac-phone" value={f.phone} onChange={set("phone")} /></Field>
              <Field label="Email"><Input data-testid="ac-email" type="email" value={f.email} onChange={set("email")} disabled={editing} /></Field>
              <Field label="Street Address"><Input data-testid="ac-street" value={f.street_address} onChange={set("street_address")} /></Field>
              <Field label="City"><Input data-testid="ac-city" value={f.city} onChange={set("city")} /></Field>
              <Field label="State">
                <Select value={f.state} onValueChange={set("state")}>
                  <SelectTrigger data-testid="ac-state"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="ZIP Code"><Input data-testid="ac-zip" value={f.zip_code} onChange={set("zip_code")} /></Field>
            </div>
          </Section>

          <Section title="Service Information (Trades)">
            <div className="flex flex-wrap gap-2" data-testid="ac-trades">
              {TRADES.map((t) => <Chip key={t} active={f.trades.includes(t)} onClick={() => toggle("trades", t)}>{t}</Chip>)}
            </div>
            <Field label="Secondary Trades (notes)"><Input data-testid="ac-secondary" value={f.secondary_trades} onChange={set("secondary_trades")} /></Field>
          </Section>

          <Section title="Coverage Area">
            <Field label="States Served">
              <div className="flex flex-wrap gap-2">{COVERAGE_STATES.map((s) => <Chip key={s} active={f.coverage_states.includes(s)} onClick={() => toggle("coverage_states", s)}>{s}</Chip>)}</div>
            </Field>
            {uniqCities.length > 0 && (
              <Field label="Cities / Counties Served">
                <div className="flex flex-wrap gap-2" data-testid="ac-cities">{uniqCities.map((c) => <Chip key={c} active={f.coverage_cities.includes(c)} onClick={() => toggle("coverage_cities", c)}>{c}</Chip>)}</div>
              </Field>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Service Radius"><Input data-testid="ac-radius" placeholder="e.g. 30 mi" value={f.service_radius} onChange={set("service_radius")} /></Field>
              <Field label="Availability">
                <Select value={f.availability_status} onValueChange={set("availability_status")}>
                  <SelectTrigger data-testid="ac-availability"><SelectValue /></SelectTrigger>
                  <SelectContent>{AVAILABILITY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Checkbox data-testid="ac-emergency" checked={f.emergency_availability} onCheckedChange={(v) => setF((p) => ({ ...p, emergency_availability: !!v }))} /> Available for emergencies
            </label>
          </Section>

          {!editing && (
            <Section title="Portal Login (optional)">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox data-testid="ac-create-login" checked={f.create_login} onCheckedChange={(v) => setF((p) => ({ ...p, create_login: !!v }))} /> Create a portal login for this contractor
              </label>
              {f.create_login && <Field label="Temporary Password"><Input data-testid="ac-password" type="text" value={f.password} onChange={set("password")} placeholder="Share this with the contractor" /></Field>}
            </Section>
          )}

          <Field label="Internal Notes"><Textarea data-testid="ac-notes" value={f.notes} onChange={set("notes")} rows={2} /></Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="ac-save" disabled={busy} onClick={submit} className="bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save Changes" : "Add Contractor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-3"><div className="text-xs uppercase tracking-wide font-bold text-slate-400">{title}</div>{children}</div>
);
const Field = ({ label, children }) => (
  <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1.5">{children}</div></div>
);
